import { map } from 'rxjs';
import { NgDompurifySanitizer } from '@tinkoff/ng-dompurify';
import { TUI_IS_MOBILE_RES, TUI_SANITIZER, TuiRootModule } from '@taiga-ui/core';
import { TuiLetModule } from '@taiga-ui/cdk';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { UserContextService } from '@iss/libs/user-context';
import { PERM } from '@iss/libs/permissions';
import { IfHasPermissionPipe, ISS_AUTH_SERVICE, PermissionsService } from '@iss/libs/auth';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  Renderer2,
} from '@angular/core';
import { AsyncPipe, NgIf, NgTemplateOutlet } from '@angular/common';

import { PageMobileAppTabComponent } from './components/page-mobile-tab-bar';

import { AppRoutingLinks } from '@wo-mc/routing';
import { PageContextBarComponent, PageMobileAppBarComponent } from '@wo-mc/components';
import {
  authActions,
  authFeature,
  globalSpinnerFeature,
  userPreferencesActions,
  userPreferencesFeature,
  appContextFeature,
  authSelectors,
} from '@wo-mc/+state';
import { environment } from '@wo-environments';
import { IsFalsePipe } from '@iss/wo-common/pipes';
import { syncStore } from '@iss/wo-common/ngrx';
import { AvatarMenuComponent, ContactSupportComponent, EnvInfoComponent, ImpersonateService } from '@iss/wo-common/components';
import {
  AvatarComponent,
  GlobalSpinnerComponent,
  PageTitleBarComponent,
  PortalHeaderComponent,
  SideNavComponent,
  SideNavContainerComponent,
  SideNavHeaderComponent,
  SideNavMenuItemComponent,
} from '@iss/core/portal';

@Component({
  selector: 'wo-root',
  templateUrl: './app.component.html',
  styleUrls: [ './app.component.scss' ],
  standalone: true,
  imports: [
    AsyncPipe,
    AvatarComponent,
    AvatarMenuComponent,
    ContactSupportComponent,
    EnvInfoComponent,
    GlobalSpinnerComponent,
    IfHasPermissionPipe,
    IsFalsePipe,
    NgIf,
    NgTemplateOutlet,
    PageContextBarComponent,
    PageMobileAppBarComponent,
    PageMobileAppTabComponent,
    PageTitleBarComponent,
    PortalHeaderComponent,
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    SideNavComponent,
    SideNavContainerComponent,
    SideNavHeaderComponent,
    SideNavMenuItemComponent,
    TranslateModule,
    TuiLetModule,
    TuiRootModule,
  ],
  providers: [
    { provide: TUI_SANITIZER, useClass: NgDompurifySanitizer },
  ],
})
export class AppComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly renderer = inject(Renderer2);
  private readonly auth = inject(ISS_AUTH_SERVICE);
  private readonly impersonateService = inject(ImpersonateService);
  private readonly userContextService = inject(UserContextService);
  private readonly destroyRef = inject(DestroyRef);
  public readonly router = inject(Router);
  public readonly permission = inject(PermissionsService);
  public readonly isMobileRes$ = inject(TUI_IS_MOBILE_RES);
  public readonly username$ = this.store.select(authFeature.selectUsername);
  public readonly email$ = this.store.select(authFeature.selectUserEmail);
  public readonly header = environment.appName;
  public readonly canSeeSwitchContext$ = this.store.select(appContextFeature.selectHasMultipleContexts);
  public readonly globalSpinnerVisible$ = this.store.select(
    globalSpinnerFeature.selectIsGlobalSpinnerVisible,
  );
  public readonly expanded$ = this.store.select(
    userPreferencesFeature.selectSideNavExpanded,
  );
  public readonly isAuthorized$ = this.store.select(authSelectors.selectIsAuthorized);
  public readonly hasRequiredSupplierContext$ = this.store.select(appContextFeature.selectHasRequiredSupplierContext);
  public readonly AppRoutingLinks = AppRoutingLinks;

  public readonly impersonateContext$ = this.permission.isSufficient(PERM['can impersonate']).pipe(map(canImpersonate => {
    const impersonated = this.impersonateService.getImpersonateId() != null;
    return {
      withImpersonation: canImpersonate || this.impersonateService.getImpersonateId() != null,
      label: impersonated ? 'auth.quit-impersonation' : 'auth.impersonation',
      impersonated,
    };
  }));

  public ngOnInit(): void {
    this.auth.bootstrap().subscribe();
    syncStore(this.store, this.renderer).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  public toggleSideNav(): void {
    this.store.dispatch(userPreferencesActions.toggleSideNav());
  }

  public logout(): void {
    this.store.dispatch(authActions.logout({ withRequest: true }));
  }

  public async onImpersonation(): Promise<void> {
    await this.impersonateService.onImpersonation();
  }

  public goToContext(): void {
    this.userContextService.open();
  }
}
